import type { Client, ClientContact } from '~/types/models';

export type ClientContactDeletionPatch = Pick<
    Client,
    'contacts' | 'contactName' | 'email' | 'phone' | 'contactNumber'
>;

function topLevelContact(client: Client, contactId: string): ClientContact | null {
    const name = String(client.contactName || '').trim();
    const email = String(client.email || '').trim().toLowerCase();
    const phone = String(client.contactNumber || client.phone || '').trim();
    if (!name && !email && !phone) return null;

    return {
        id: contactId,
        name: name || email.split('@')[0] || 'Client contact',
        email,
        ...(phone ? { phone } : {}),
        isPrimary: true,
        portalRole: 'client_owner',
        projectAccessMode: 'all',
        projectIds: [],
    };
}

export function buildClientContactDeletionPatch(
    client: Client,
    contactId: string,
): ClientContactDeletionPatch | null {
    const storedContacts = Array.isArray(client.contacts) ? client.contacts.map(contact => ({ ...contact })) : [];
    const contacts = storedContacts.length > 0
        ? storedContacts
        : [topLevelContact(client, contactId)].filter((contact): contact is ClientContact => Boolean(contact));
    const targetIndex = contacts.findIndex(contact => contact.id === contactId);
    if (targetIndex < 0) return null;

    const target = contacts[targetIndex];
    let remaining = contacts.filter((_, index) => index !== targetIndex);
    if (target?.isPrimary) {
        const promotedId = remaining.find(contact => contact.isPrimary)?.id || remaining[0]?.id;
        remaining = remaining.map(contact => ({
            ...contact,
            ...(contact.id === promotedId ? { isPrimary: true } : { isPrimary: false }),
        }));
    }

    const primary = remaining.find(contact => contact.isPrimary) || remaining[0] || null;
    return {
        contacts: remaining,
        contactName: primary?.name || '',
        email: String(primary?.email || '').trim().toLowerCase(),
        phone: primary?.phone || '',
        contactNumber: primary?.phone || '',
    };
}
